/*
 * combo-sticker-builder.js
 * Drives the Combo Sticker Builder block in product-heylander.liquid.
 *
 * - Customer picks one TOP + one BOTTOM sticker from compact image tiles.
 * - When both are chosen we look up the matching combo image and swap the
 *   existing gallery featured image (storing the original so it can be restored).
 * - "Add combo to cart" posts BOTH selected products in one /cart/add.js request.
 *
 * Vanilla IIFE, initialised by [data-combo-builder] data attributes. Multi-instance safe.
 * Made by Jonno jonathan@birubi.co
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Money formatting (mirrors Shopify's Shopify.formatMoney)
   * ------------------------------------------------------------------ */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function build(precision, thousands, decimal) {
      if (isNaN(cents) || cents == null) return '0';
      var n = (cents / 100.0).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount':
        value = build(2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = build(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = build(2, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = build(2, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = build(2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = build(0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = build(0, ' ', '');
        break;
      default:
        value = build(2, ',', '.');
    }

    return formatString.replace(placeholderRegex, value);
  }

  /* ------------------------------------------------------------------ *
   * Per-instance initialiser
   * ------------------------------------------------------------------ */
  function initComboBuilder(root) {
    if (!root || root.dataset.comboInit === 'true') return;
    root.dataset.comboInit = 'true';

    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var comboTitle = root.getAttribute('data-combo-product-title') || '';
    var btnTextDefault = root.getAttribute('data-button-text') || 'Add combo to cart';
    var showFrom = root.getAttribute('data-show-from') === 'true';
    var hideNative = root.getAttribute('data-hide-native-buy') === 'true';
    var fromPrice = parseInt(root.getAttribute('data-from-price'), 10);

    var topGrid = root.querySelector('[data-combo-grid="top"]');
    var bottomGrid = root.querySelector('[data-combo-grid="bottom"]');
    var topLabel = root.querySelector('[data-combo-step="top"] .radio__legend__label');
    var bottomLabel = root.querySelector('[data-combo-step="bottom"] .radio__legend__label');
    var topValueEl = root.querySelector('[data-combo-step-value="top"]');
    var bottomValueEl = root.querySelector('[data-combo-step-value="bottom"]');
    var summaryWrap = root.querySelector('[data-combo-summary-wrap]');
    var summaryEl = root.querySelector('[data-combo-summary]');
    var priceEl = root.querySelector('[data-combo-price]');
    var errorEl = root.querySelector('[data-combo-error]');
    var addBtn = root.querySelector('[data-combo-add]');
    var btnTextEl = root.querySelector('[data-combo-btn-text]');
    var sepEl = root.querySelector('[data-combo-sep]');
    var priceInlineEl = root.querySelector('[data-combo-price-inline]');
    var compareEl = root.querySelector('[data-combo-compare]');
    var badgeEl = root.querySelector('[data-combo-badge]');
    var previewBtn = root.querySelector('[data-combo-preview-open]');
    var previewImg = root.querySelector('[data-combo-preview-img]');
    var lightbox = root.querySelector('[data-combo-lightbox]');
    var lightboxImg = root.querySelector('[data-combo-lightbox-img]');
    var lightboxCaption = root.querySelector('[data-combo-lightbox-caption]');
    var currentCombo = null;

    function fmtSavings(cents) {
      return formatMoney(cents, moneyFormat).replace(/[.,]00(?=\D|$)/, '');
    }

    var topPrompt = (topLabel && topLabel.textContent.trim()) || 'Choose your top sticker';
    var bottomPrompt = (bottomLabel && bottomLabel.textContent.trim()) || 'Choose your bottom sticker';

    var imageMap = {};
    try {
      var mapEl = root.querySelector('[data-combo-image-map]');
      if (mapEl) imageMap = JSON.parse(mapEl.textContent || '{}');
    } catch (e) {
      console.warn('[combo] could not parse combo image map', e);
    }

    var section = root.closest('[data-section-id]') || document;
    var selected = { top: null, bottom: null };

    /* ---- Gallery featured image swap ------------------------------- */
    function getFeaturedImg() {
      var slideshow = section.querySelector('[data-product-slideshow]') ||
        document.querySelector('[data-product-slideshow]');
      if (!slideshow) return null;
      return (
        slideshow.querySelector('[data-media-slide][data-type="image"] .lazy-image img') ||
        slideshow.querySelector('.product__media img') ||
        slideshow.querySelector('img')
      );
    }

    function updateComboFeaturedImage(imageUrl, altText) {
      var img = getFeaturedImg();
      if (!img || !imageUrl) return;
      if (img.dataset.comboOrigSrc === undefined) {
        img.dataset.comboOrigSrc = img.getAttribute('src') || '';
        img.dataset.comboOrigSrcset = img.getAttribute('srcset') || '';
        img.dataset.comboOrigSizes = img.getAttribute('sizes') || '';
        img.dataset.comboOrigAlt = img.getAttribute('alt') || '';
      }
      img.removeAttribute('srcset');
      img.setAttribute('src', imageUrl);
      if (altText != null) img.setAttribute('alt', altText);
    }

    function restoreOriginalFeaturedImage() {
      var img = getFeaturedImg();
      if (!img || img.dataset.comboOrigSrc === undefined) return;
      if (img.dataset.comboOrigSrcset) img.setAttribute('srcset', img.dataset.comboOrigSrcset);
      else img.removeAttribute('srcset');
      if (img.dataset.comboOrigSizes) img.setAttribute('sizes', img.dataset.comboOrigSizes);
      img.setAttribute('src', img.dataset.comboOrigSrc);
      img.setAttribute('alt', img.dataset.comboOrigAlt || '');
    }

    function applyComboImage() {
      if (!selected.top || !selected.bottom) {
        restoreOriginalFeaturedImage();
        hidePreview();
        return;
      }
      var key = selected.top.variantId + '|' + selected.bottom.variantId;
      var entry = imageMap[key];
      var caption = selected.top.name + ' + ' + selected.bottom.name;
      if (entry && entry.image) {
        var alt = entry.alt || caption;
        updateComboFeaturedImage(entry.image, alt);
        setPreview(entry.image, alt, caption);
      } else {
        // Missing combo image: keep current image, no customer-facing error.
        console.warn('[combo] no combo image for key:', key);
        hidePreview();
      }
    }

    /* ---- Combo preview thumbnail + lightbox ------------------------ */
    function setPreview(image, alt, caption) {
      currentCombo = { image: image, alt: alt, caption: caption };
      if (previewImg) {
        previewImg.src = image;
        previewImg.alt = alt;
      }
      if (previewBtn) previewBtn.hidden = false;
    }

    function hidePreview() {
      currentCombo = null;
      if (previewBtn) previewBtn.hidden = true;
    }

    function openLightbox() {
      if (!currentCombo || !lightbox) return;
      if (lightboxImg) {
        lightboxImg.src = currentCombo.image;
        lightboxImg.alt = currentCombo.alt;
      }
      if (lightboxCaption) lightboxCaption.textContent = currentCombo.caption;
      lightbox.hidden = false;
      document.documentElement.classList.add('cksc-combo-lock');
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      document.documentElement.classList.remove('cksc-combo-lock');
    }

    if (previewBtn) previewBtn.addEventListener('click', openLightbox);
    if (lightbox) {
      // Move to <body> so position:fixed isn't trapped by a transformed ancestor.
      if (lightbox.parentNode !== document.body) document.body.appendChild(lightbox);
      lightbox.addEventListener('click', function (e) {
        if (e.target.closest('[data-combo-lightbox-close]')) closeLightbox();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !lightbox.hidden) closeLightbox();
      });
    }

    /* ---- UI state -------------------------------------------------- */
    function hideError() {
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
    }
    function showError(msg) {
      if (errorEl) {
        errorEl.textContent = msg;
        errorEl.hidden = false;
      }
    }

    function bothSelected() {
      return selected.top && selected.bottom;
    }
    function bothAvailable() {
      return bothSelected() && selected.top.available && selected.bottom.available;
    }

    function updateSummary() {
      if (bothSelected()) {
        if (summaryEl) summaryEl.textContent = selected.top.name + ' + ' + selected.bottom.name;
        if (summaryWrap) summaryWrap.hidden = false;
      } else if (summaryWrap) {
        summaryWrap.hidden = true;
      }
    }

    function updateStepValues() {
      if (topValueEl) topValueEl.textContent = selected.top ? selected.top.name : '';
      if (bottomValueEl) bottomValueEl.textContent = selected.bottom ? selected.bottom.name : '';
    }

    function hideEl(el) {
      if (el) el.hidden = true;
    }

    function updateButtonAndPrice() {
      if (!addBtn) return;
      var ready = bothSelected();

      // Standalone "From" line — only before a full selection
      if (priceEl) {
        if (!ready && showFrom && !isNaN(fromPrice)) {
          priceEl.textContent = 'From ' + formatMoney(fromPrice, moneyFormat);
          priceEl.hidden = false;
        } else {
          priceEl.textContent = '';
          priceEl.hidden = true;
        }
      }

      // Button label + enabled state
      var label;
      var enabled = false;
      if (!selected.top) {
        label = topPrompt;
      } else if (!selected.bottom) {
        label = bottomPrompt;
      } else if (!bothAvailable()) {
        label = 'Unavailable';
      } else {
        label = btnTextDefault;
        enabled = true;
      }
      if (btnTextEl) btnTextEl.textContent = label;
      addBtn.disabled = !enabled;

      // Inline price / compare / savings badge (birubi v2 style)
      if (!enabled) {
        hideEl(sepEl);
        hideEl(priceInlineEl);
        hideEl(compareEl);
        hideEl(badgeEl);
        return;
      }

      var price = selected.top.price + selected.bottom.price;
      var topCmp = selected.top.compare > selected.top.price ? selected.top.compare : selected.top.price;
      var botCmp = selected.bottom.compare > selected.bottom.price ? selected.bottom.compare : selected.bottom.price;
      var compare = topCmp + botCmp;
      var hasDiscount = compare > price;

      if (sepEl) sepEl.hidden = false;
      if (priceInlineEl) {
        priceInlineEl.textContent = formatMoney(price, moneyFormat);
        priceInlineEl.hidden = false;
      }
      if (compareEl) {
        if (hasDiscount) {
          compareEl.textContent = formatMoney(compare, moneyFormat);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (badgeEl) {
        if (hasDiscount) {
          badgeEl.textContent = 'Save ' + fmtSavings(compare - price);
          badgeEl.hidden = false;
        } else {
          badgeEl.hidden = true;
        }
      }
    }

    function refresh() {
      updateSummary();
      updateStepValues();
      updateButtonAndPrice();
      applyComboImage();
    }

    /* ---- Selection ------------------------------------------------- */
    function selectTile(btn) {
      if (btn.getAttribute('data-available') !== 'true') return;
      hideError();
      var role = btn.getAttribute('data-role');
      var grid = role === 'top' ? topGrid : bottomGrid;
      var tiles = grid.querySelectorAll('[data-combo-option]');
      for (var i = 0; i < tiles.length; i++) {
        var on = tiles[i] === btn;
        tiles[i].classList.toggle('is-selected', on);
        // Check the native radio — drives the theme's selected ring (input:checked ~ label)
        var input = tiles[i].querySelector('[data-combo-input]');
        if (input && !input.disabled) input.checked = on;
      }
      selected[role] = {
        variantId: btn.getAttribute('data-variant-id'),
        price: parseInt(btn.getAttribute('data-price'), 10) || 0,
        compare: parseInt(btn.getAttribute('data-compare'), 10) || 0,
        title: btn.getAttribute('data-title') || '',
        name: btn.getAttribute('data-name') || '',
        available: btn.getAttribute('data-available') === 'true'
      };
      refresh();
    }

    function onGridClick(e) {
      var btn = e.target.closest('[data-combo-option]');
      if (btn) selectTile(btn);
    }
    if (topGrid) topGrid.addEventListener('click', onGridClick);
    if (bottomGrid) bottomGrid.addEventListener('click', onGridClick);

    /* ---- Add to cart ----------------------------------------------- */
    function openCartDrawer(cart) {
      // Update header / cart UI
      document.dispatchEvent(
        new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true })
      );
      // Open the drawer the canonical way, with fallbacks.
      var toggle = document.querySelector('[data-drawer-toggle="drawer-cart"]');
      if (toggle) {
        toggle.click();
        return;
      }
      var drawer = document.querySelector('[data-drawer="drawer-cart"]');
      if (drawer) {
        drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
        return;
      }
      window.location.href = '/cart';
    }

    function addToCart() {
      if (!bothAvailable()) return;
      hideError();
      addBtn.disabled = true;
      addBtn.classList.add('loading');

      var payload = {
        items: [
          {
            id: Number(selected.top.variantId),
            quantity: 1,
            properties: {
              _Combo: comboTitle,
              '_Combo Role': 'Top',
              '_Combo Partner': selected.bottom.title
            }
          },
          {
            id: Number(selected.bottom.variantId),
            quantity: 1,
            properties: {
              _Combo: comboTitle,
              '_Combo Role': 'Bottom',
              '_Combo Partner': selected.top.title
            }
          }
        ]
      };

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data && data.description ? data.description : 'Could not add to cart');
            return data;
          });
        })
        .then(function () {
          return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
            return r.json();
          });
        })
        .then(function (cart) {
          addBtn.classList.remove('loading');
          updateButtonAndPrice();
          openCartDrawer(cart);
        })
        .catch(function (err) {
          console.warn('[combo] add to cart failed', err);
          addBtn.classList.remove('loading');
          showError('Sorry, we couldn’t add this combo to your cart. Please try again.');
          updateButtonAndPrice();
        });
    }

    if (addBtn) addBtn.addEventListener('click', addToCart);

    /* ---- Optional: hide native variant picker + buy button --------- */
    if (hideNative) {
      var form = section.querySelector('[data-product-form]');
      if (form) {
        var formBlock = form.closest('.product__block') || form.closest('product-form') || form;
        if (formBlock) formBlock.style.display = 'none';
      }
      var variantBlock = section.querySelector('[data-product-options], .product__block__variants, .product-form__variants');
      if (variantBlock) variantBlock.style.display = 'none';
    }

    /* ---- Initial paint --------------------------------------------- */
    refresh();
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-combo-builder]');
    for (var i = 0; i < roots.length; i++) initComboBuilder(roots[i]);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
})();